import { IS_CLIENT } from "@shared/constants";

let widgetConfig = {
  apiKey: null,
};

const initGoogleMapsWidget = config => {
  widgetConfig = config;
};

const GOOGLE_MAPS_CALLBACK = "__o2cGoogleMapsLoaded";

let googleMapsPromise = null;
let markerClustererPromise = null;

const assertInitialized = methodName => {
  if (!widgetConfig.apiKey) {
    throw new Error(
      `initGoogleMapsWidget must be called before calling ${methodName}`
    );
  }
};

// window.google.maps is created by the bootstrap script before the core API and
// the requested libraries (places, geometry) finish loading, so its mere presence
// must not be treated as "ready".
const isGoogleMapsReady = () =>
  IS_CLIENT &&
  !!window.google?.maps?.Map &&
  !!window.google.maps.geometry?.spherical &&
  !!window.google.maps.places;

const injectScript = (src, onError) => {
  const script = document.createElement("script");

  script.async = true;
  script.onerror = onError;
  script.src = src;

  window.document.head.appendChild(script);
};

// Language is taken from the first call only - Maps API can't be loaded twice
// on one page (different language => different URL => duplicate API load).
const lazyLoadGoogleMaps = language => {
  assertInitialized("lazyLoadGoogleMaps");

  if (isGoogleMapsReady()) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const fail = error => {
      googleMapsPromise = null;
      delete window[GOOGLE_MAPS_CALLBACK];
      reject(error || new Error("Google Maps script failed to load"));
    };

    // API loaded by someone else (e.g. 3rd party tag) without our libraries
    if (window.google?.maps?.importLibrary) {
      Promise.all([
        window.google.maps.importLibrary("maps"),
        window.google.maps.importLibrary("marker"),
        window.google.maps.importLibrary("places"),
        window.google.maps.importLibrary("geometry"),
      ])
        .then(() => resolve(window.google.maps))
        .catch(fail);

      return;
    }

    window[GOOGLE_MAPS_CALLBACK] = () => {
      delete window[GOOGLE_MAPS_CALLBACK];
      resolve(window.google.maps);
    };

    injectScript(
      `https://maps.googleapis.com/maps/api/js?key=${
        widgetConfig.apiKey
      }&v=3.exp&libraries=places,geometry&callback=${GOOGLE_MAPS_CALLBACK}${
        language ? `&language=${language}` : ""
      }`,
      fail
    );
  });

  return googleMapsPromise;
};

const lazyLoadMarkerClusterer = () => {
  assertInitialized("lazyLoadMarkerClusterer");

  if (IS_CLIENT && window.MarkerClusterer) {
    return Promise.resolve(window.MarkerClusterer);
  }

  if (markerClustererPromise) {
    return markerClustererPromise;
  }

  markerClustererPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");

    script.async = true;
    script.onload = () => resolve(window.MarkerClusterer);
    script.onerror = error => {
      markerClustererPromise = null;
      reject(error);
    };
    script.src = `https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js`;

    window.document.head.appendChild(script);
  });

  return markerClustererPromise;
};

module.exports = {
  initGoogleMapsWidget,
  lazyLoadGoogleMaps,
  lazyLoadMarkerClusterer,
};
